export interface GridLayout {
  cols: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
}

/**
 * Picks the column count that gives the largest tiles of the given aspect ratio
 * for `count` tiles inside a `width`×`height` box.
 */
export function bestGrid(count: number, width: number, height: number, gap = 8, ratio = 16 / 9): GridLayout {
  if (count <= 0 || width <= 0 || height <= 0) return { cols: 1, rows: 1, tileWidth: 0, tileHeight: 0 };
  let best: GridLayout = { cols: 1, rows: count, tileWidth: 0, tileHeight: 0 };
  for (let cols = 1; cols <= count; cols++) {
    const rows = Math.ceil(count / cols);
    const maxW = (width - gap * (cols - 1)) / cols;
    const maxH = (height - gap * (rows - 1)) / rows;
    const tileWidth = Math.floor(Math.min(maxW, maxH * ratio));
    if (tileWidth > best.tileWidth) best = { cols, rows, tileWidth, tileHeight: Math.floor(tileWidth / ratio) };
  }
  return best;
}

/** Most tiles a gallery page shows (Zoom's default is 25). */
export const MAX_PAGE = 25;

/**
 * How many tiles fit on one gallery page in a `width`×`height` box while every tile stays at least
 * `minWidth` wide: 25 on a large screen, fewer on a laptop, 4 on a phone.
 */
export function pageSizeFor(width: number, height: number, minWidth = 180): number {
  if (width <= 0 || height <= 0) return MAX_PAGE;
  // Phones decode few videos well, whatever fits on the screen.
  const cap = width < 600 ? 4 : MAX_PAGE;
  for (let n = cap; n > 1; n--) if (bestGrid(n, width, height).tileWidth >= Math.min(minWidth, width / 2 - 8)) return n;
  return 1;
}

/** How recently someone must have spoken to be brought to the first page. */
export const RECENT_SPEAKER_MS = 5 * 60_000;

/**
 * Stage order for large meetings: me first, then the people who spoke recently (so the
 * conversation is always on the first page), then everyone else by arrival. Within each group the
 * order is by arrival, so tiles do not jump around every time someone else talks.
 */
export function stageOrder<P extends { id: string; joinedAt: number }>(
  people: P[],
  selfId: string,
  spokeAt: Record<string, number>,
  firstPage: number,
  now: number,
): P[] {
  const self = people.filter((p) => p.id === selfId);
  const others = people.filter((p) => p.id !== selfId).sort((a, b) => a.joinedAt - b.joinedAt);
  const room = Math.max(0, firstPage - self.length);
  const recent = others
    .filter((p) => now - (spokeAt[p.id] ?? -Infinity) < RECENT_SPEAKER_MS)
    .sort((a, b) => (spokeAt[b.id] ?? 0) - (spokeAt[a.id] ?? 0))
    .slice(0, room);
  const promoted = new Set(recent.map((p) => p.id));
  return [...self, ...others.filter((p) => promoted.has(p.id)), ...others.filter((p) => !promoted.has(p.id))];
}

/** Splits an ordered list into pages of `size`. */
export function paginate<T>(items: T[], size: number): T[][] {
  const pages: T[][] = [];
  for (let i = 0; i < items.length; i += Math.max(1, size)) pages.push(items.slice(i, i + Math.max(1, size)));
  return pages.length ? pages : [[]];
}

/**
 * Gallery order that stays put, as in Zoom: people keep their place (newcomers at the end), and
 * only someone who speaks while off the first page is brought onto it, swapping places with the
 * person on the first page who has been quiet the longest. One swap per call.
 */
export function stableOrder<P extends { id: string; joinedAt: number }>(
  prev: string[],
  people: P[],
  selfId: string,
  speaking: string[],
  spokeAt: Record<string, number>,
  firstPage: number,
  allowSwap: boolean,
): string[] {
  const present = new Set(people.map((p) => p.id));
  const kept = prev.filter((id) => present.has(id) && id !== selfId);
  const known = new Set(kept);
  const added = people.filter((p) => p.id !== selfId && !known.has(p.id)).sort((a, b) => a.joinedAt - b.joinedAt).map((p) => p.id);
  const order = [...(present.has(selfId) ? [selfId] : []), ...kept, ...added];
  if (!allowSwap || order.length <= firstPage) return order;
  const offPage = speaking
    .map((id) => ({ id, i: order.indexOf(id) }))
    .filter((x) => x.i >= firstPage)
    .sort((a, b) => (spokeAt[b.id] ?? 0) - (spokeAt[a.id] ?? 0))[0];
  if (!offPage) return order;
  const talking = new Set(speaking);
  let victim = -1;
  for (let i = 0; i < Math.min(firstPage, order.length); i++) {
    const id = order[i];
    if (id === selfId || talking.has(id)) continue;
    if (victim < 0 || (spokeAt[id] ?? -Infinity) <= (spokeAt[order[victim]] ?? -Infinity)) victim = i;
  }
  if (victim < 0) return order;
  [order[victim], order[offPage.i]] = [order[offPage.i], order[victim]];
  return order;
}
